import { Box, type LayoutProps } from "@chakra-ui/react";
import { DemoSuccessContent } from "components/demo-form/demo-success-content";
import { type DemoLinkParameters, useDemoLinkWithParameters } from "hooks/use-demo-link";
import React, { useState } from "react";
import { Button, type ButtonProps } from "ui/button";
import { HubSpotFormModal } from "../hubspot";
import { trackDemoButtonClicked, trackDemoFormSubmitted } from "./utils";

type BookDemoButtonProps = {
  /**
   * Button text.
   *
   * @default "Get a demo"
   */
  text?: string;

  /**
   * Button variant.
   *
   * @default "primary"
   */
  variant?: ButtonProps["variant"];

  /**
   * Button pulse.
   */
  pulse?: ButtonProps["pulse"];

  /**
   * Custom Chili Piper router to use with the demo modal.
   *
   * @default "inbound-router"
   */
  chiliPiperRouterName?: string;

  /**
   * Option to render a link to the /demo page instead of using the demo modal.
   */
  isLink?: boolean;

  /**
   * Custom parameters to use when building the demo link.
   */
  demoLinkParameters?: DemoLinkParameters;
} & Pick<LayoutProps, "display">;

/**
 * By default, provides a button that launches a modal to book a demo.
 *
 * Optionally, provides a button-styled link to the book demo page that tracks the "Demo Button Clicked" conversion event.
 */
export function BookDemoButton({
  text = "Get a demo",
  variant = "primary",
  chiliPiperRouterName = "inbound-router",
  pulse,
  isLink,
  demoLinkParameters,
  display,
}: BookDemoButtonProps) {
  const link = useDemoLinkWithParameters(demoLinkParameters);

  if (isLink) {
    return (
      <Button
        variant={variant}
        hasRightChevron={variant === "primary"}
        href={link}
        display={display}
        pulse={pulse}
        onClick={trackDemoButtonClicked}
      >
        {text}
      </Button>
    );
  }

  return (
    // Wrap modal in relative position div to avoid UI shifting on click
    <Box pos="relative" display={display}>
      <BookDemoModal
        launchButtonVariant={variant}
        label={text}
        chiliPiperRouterName={chiliPiperRouterName}
        launchButtonPulse={pulse}
      />
    </Box>
  );
}

type ModalProps = {
  label: string;
  launchButtonVariant: ButtonProps["variant"];
  launchButtonPulse?: ButtonProps["pulse"];
  chiliPiperRouterName: string;
};

function BookDemoModal({ label, launchButtonVariant, launchButtonPulse, chiliPiperRouterName }: ModalProps) {
  const [success, setSuccess] = useState<boolean>(false);
  const [isPrivateBrowser, setIsPrivateBrowser] = useState<boolean>(false);

  const onFormSubmit = (lead: Record<string, string>) => {
    const cpTenantDomain = "hightouch";

    trackDemoFormSubmitted(lead);

    if (window.ChiliPiper) {
      window.ChiliPiper.submit?.(cpTenantDomain, chiliPiperRouterName, {
        map: true,
        lead,
        onSuccess() {
          setSuccess(true);
        },
        onError() {
          // Show the success modal even if ChiliPiper errors because the lead is still created.
          setSuccess(true);
        },
      });
    } else {
      // IF CP does not load (ex. private safari windows),
      // we still show the success content to let the user know we have received their request through Hubspot
      setSuccess(true);
      setIsPrivateBrowser(true);
    }
  };

  return (
    <HubSpotFormModal
      label={label}
      formId="6ba03127-8b95-400c-a229-c5fb099d929f"
      sfdcCampaignId="7015e0000010D1QAAU"
      hasLaunchButtonChevron={launchButtonVariant === "primary"}
      launchButtonVariant={launchButtonVariant}
      launchButtonPulse={launchButtonPulse}
      modalLabel="Get a demo"
      modalHeading="Schedule a 30-minute demo with a product expert"
      modalDescription="Learn how Hightouch can solve your use cases."
      successContent={success && <DemoSuccessContent isPrivateBrowser={isPrivateBrowser} />}
      onSubmit={onFormSubmit}
    />
  );
}
